import { LSP as SharedLSP } from '@prophecy/shared-core/lsp';

// oxlint's unused-imports rule doesn't traverse into namespace bodies, so `Callable` — used by
// ExtractParams / MethodType below — gets flagged as unused. Alias it at module scope so the
// reference is visible outside the namespace and the auto-fix stops stripping it.
import type { Callable as CallableImport } from '@prophecy/interfaces/generic';
type Callable = CallableImport;

// Re-export the shared LSP namespace + augment with FE-only React hook helpers below.
// The wire vocabulary (Method / Notification enums, Error / Success / CallbackReturnType) now
// lives in @prophecy/shared-core/lsp so prophecy-mcp and any future Node consumer speak the same
// protocol without duplicating the enum. Only helpers that depend on FE-only `Callable` stay here.
export namespace LSP {
  export import Notification = SharedLSP.Notification;
  export import Method = SharedLSP.Method;
  export type Error = SharedLSP.Error;
  export type Success = SharedLSP.Success;
  export type CallbackReturnType = SharedLSP.CallbackReturnType;

  export type ExtractParams<C> = C extends Callable ? Parameters<C> : [unknown];
  export type ExtractReturnType<C> = C extends Callable ? ReturnType<C> : never;
  /** Resolved (awaited) return type of the inner LSP method callback */
  export type ExtractResolvedReturnType<C> = C extends Callable ? Awaited<ReturnType<C>> : never;
  /** Union of all possible return shapes from the hook's method (readonly, datasetInfo, success). */
  export type MethodReturnType<D, TMethod extends Method = Method> = {
    error: Error | undefined;
    data: D | undefined;
    isLoading: boolean | undefined;
    isError: boolean | undefined;
    type: TMethod;
  };

  export type MethodType<C extends Callable> =
    ReturnType<C> extends Callable
      ? (
          ...args: Parameters<ReturnType<C>>
        ) => Promise<MethodReturnType<ExtractResolvedReturnType<ReturnType<C>>, Method>>
      : never;
}
